import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";
import {
  LogIn,
  LogOut,
  Calendar,
  Clock,
  MapPin,
  RefreshCw,
  Filter,
} from "lucide-react";

export default function AttendanceHome() {
  const navigate = useNavigate();
  const { user, roles } = useContext(AuthContext);
  const [attendanceData, setAttendanceData] = useState([]);
  const token = getToken();
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [notification, setNotification] = useState(null);

  // New states for approvers modal
  const [showApproversModal, setShowApproversModal] = useState(false);
  const [approversData, setApproversData] = useState([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  const handleClock = (type) => {
    const route = getRoleBasedRouteLocal(
      `/admin/attendance_management/mob-attendance/camera/${encodeURIComponent(type)}`,
    );
    navigate(route);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return ""; // Return empty string for null/undefined
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    if (s === "approved") {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#16C47F] text-white">
          Approved
        </span>
      );
    } else if (s === "pending") {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#FFC400] text-white">
          Pending
        </span>
      );
    } else if (s === "rejected") {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500 text-white dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800">
          Rejected
        </span>
      );
    }
    return <span className="text-gray-400">-</span>;
  };

  useEffect(() => {
    fetchAttendance();
  }, [token]);

  // fetch records
  const fetchAttendance = async () => {
    try {
      const response = await axios.get(
        "https://hrmspayroll.prepstripe.com/api/attendance",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setAttendanceData(response.data.data?.data || []);
    } catch (error) {
      console.error("Error fetching attendance records:", error);
    }
  };

  // New function to fetch approvers data
  const fetchMyApprovers = async () => {
    setLoadingApprovers(true);
    try {
      const response = await axios.get(
        "https://hrmspayroll.prepstripe.com/api/approval/my-approvers",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setApproversData(response?.data?.data?.data || []);
      setShowApproversModal(true);
    } catch (error) {
      console.error("Error fetching approvers:", error);
      setNotification({
        message:
          error?.response?.data?.error || "Error fetching approvers data",
        type: "error",
      });
    } finally {
      setLoadingApprovers(false);
    }
  };

  // Function to handle approval resolution
  const resolveApproval = async (approvalId, status) => {
    try {
      const response = await axios.post(
        "https://hrmspayroll.prepstripe.com/api/approval/resolve",
        {
          approval_id: approvalId,
          status: status,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setNotification({
        message: "Approval resolved successfully",
        type: "success",
      });

      // Refresh approvers data
      fetchMyApprovers();
    } catch (error) {
      console.error("Error resolving approval:", error);
      setNotification({
        message: error?.response?.data?.error || "Error resolving approval",
        type: "error",
      });
    }
  };

  // attendance filter
  const fetchAttendanceRecords = async () => {
    if (!filterFromDate) {
      setNotification({
        message: "Select from date",
        type: "error",
      });
      return;
    }
    if (!filterToDate) {
      setNotification({
        message: "Select to date",
        type: "error",
      });
      return;
    }
    if (!filterType) {
      setNotification({
        message: "Select filter type",
        type: "error",
      });
      return;
    }
    try {
      const response = await axios.get(
        `https://hrmspayroll.prepstripe.com/api/attendance/filter`,
        {
          params: {
            from_date: filterFromDate,
            to_date: filterToDate,
            type: filterType,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Filter only approved records
      const approvedRecords = (response.data?.data || []).filter((record) => {
        const checkInApproval = record.approvals.find(
          (a) => a.approval_type === "check_in",
        );
        const checkOutApproval = record.approvals.find(
          (a) => a.approval_type === "check_out",
        );

        return (
          (checkInApproval && checkInApproval.approval_status === "approved") ||
          (checkOutApproval && checkOutApproval.approval_status === "approved")
        );
      });

      setAttendanceData(approvedRecords);
    } catch (error) {
      console.error(
        "Error fetching attendance:",
        error.response?.data?.error || error.message,
      );
      setNotification({
        message: `${error?.response?.data?.error || "Got an error while filtering the data"}`,
        type: "error",
      });
    }
  };

  // Function to check if the date is today
  const isToday = (isoString) => {
    const today = new Date();
    const recordDate = new Date(isoString);
    return (
      today.getDate() === recordDate.getDate() &&
      today.getMonth() === recordDate.getMonth() &&
      today.getFullYear() === recordDate.getFullYear()
    );
  };

  const latestRecord = attendanceData.length > 0 ? attendanceData[0] : null;

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4 font-poppins" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">
                Attendance
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Attendance
        </h2>
      </div>

      {/* Clock In/Out Buttons */}
      <div className="flex justify-center gap-4 mb-8 font-poppins">
        <button
          onClick={() => {
            const hasManager =
              user?.manager || user?.reporting_to || user?.manager_id;
            const canSkipManager = roles?.some((r) =>
              ["Admin", "HR"].includes(r),
            );

            if (!hasManager && !canSkipManager) {
              setNotification({
                message: "Manager is not allocated.",
                type: "error",
              });
              return;
            }
            handleClock("Clock In");
          }}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3.5 rounded-xl font-semibold shadow-lg shadow-emerald-200 dark:shadow-none transition-all hover:translate-y-[-2px] active:translate-y-[0]"
        >
          <LogIn className="w-5 h-5" />
          Clock In
        </button>

        <button
          onClick={() => {
            const hasManager =
              user?.manager || user?.reporting_to || user?.manager_id;
            const canSkipManager = roles?.some((r) =>
              ["Admin", "HR"].includes(r),
            );

            if (!hasManager && !canSkipManager) {
              setNotification({
                message: "Manager is not allocated.",
                type: "error",
              });
              return;
            }
            handleClock("Clock Out");
          }}
          className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-3.5 rounded-xl font-semibold shadow-lg shadow-rose-200 dark:shadow-none transition-all hover:translate-y-[-2px] active:translate-y-[0]"
        >
          <LogOut className="w-5 h-5" />
          Clock Out
        </button>
      </div>

      <div>
        {/* Title and Filter Section */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center font-poppins mb-6 gap-4">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
            Attendance Records
          </h3>

          {/* Filter Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <Filter className="w-4 h-4" />
              Filters:
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
                <label
                  className="text-sm font-medium text-gray-600 dark:text-gray-400"
                  htmlFor="from_date"
                >
                  Start
                </label>
                <input
                  id="from_date"
                  type="date"
                  value={filterFromDate}
                  required
                  title="Select From date"
                  onChange={(e) => setFilterFromDate(e.target.value)}
                  className="px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg border-2 border-gray-200 focus:outline-none focus:border-blue-500 text-gray-700 transition-all w-full sm:w-auto text-sm"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
                <label
                  className="text-sm font-medium text-gray-600 dark:text-gray-400"
                  htmlFor="to_date"
                >
                  End
                </label>
                <input
                  id="to_date"
                  type="date"
                  value={filterToDate}
                  title="Select To Date"
                  onChange={(e) => setFilterToDate(e.target.value)}
                  className="px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg border-2 border-gray-200 focus:outline-none focus:border-blue-500 text-gray-700 transition-all w-full sm:w-auto text-sm"
                />
              </div>

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg border-2 border-gray-200 focus:outline-none focus:border-blue-500 text-gray-700 transition-all w-full sm:w-auto text-sm font-medium"
              >
                <option value="all">All</option>
                <option value="check_in">Check In</option>
                <option value="check_out">Check Out</option>
              </select>

              <button
                onClick={fetchAttendanceRecords}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-all shadow-md w-full sm:w-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Apply
              </button>
            </div>
          </div>
        </div>

        {/* Attendance Records Table */}
        <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
          <table className="min-w-full table-auto md:table-fixed text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 border-b dark:border-gray-700">
              <tr>
                <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Sr No.
                </th>
                <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date
                  </div>
                </th>
                {filterType !== "check_out" && (
                  <>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <Clock className="w-4 h-4" />
                        Check In
                      </div>
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider w-48">
                      <div className="flex items-center justify-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Location
                      </div>
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Approval
                    </th>
                  </>
                )}
                {filterType !== "check_in" && (
                  <>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <Clock className="w-4 h-4" />
                        Check Out
                      </div>
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider w-48">
                      <div className="flex items-center justify-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Location
                      </div>
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Approval
                    </th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {attendanceData.length > 0 ? (
                attendanceData.map((record, index) => {
                  const checkInApproval = record.approvals.find(
                    (a) => a.approval_type === "check_in",
                  )?.approval_status;

                  const checkOutApproval = record.approvals.find(
                    (a) => a.approval_type === "check_out",
                  )?.approval_status;

                  return (
                    <tr
                      key={record.id}
                      className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-center text-gray-700 dark:text-gray-300 whitespace-nowrap font-medium">
                        {index + 1}
                      </td>
                      <td className="py-3 px-4 text-sm text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatDate(record.check_in_time)}
                      </td>
                      {filterType !== "check_out" && (
                        <>
                          <td className="py-3 px-4 text-sm text-center text-gray-700 dark:text-gray-300 whitespace-nowrap font-semibold">
                            {formatTime(record.check_in_time)}
                          </td>
                          <td className="py-3 px-4 text-sm text-center text-gray-600 dark:text-gray-400 w-48">
                            <div
                              className="w-48 truncate overflow-hidden whitespace-nowrap mx-auto"
                              title={record.check_in_location}
                            >
                              {record.check_in_location || "-"}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            {getStatusBadge(checkInApproval)}
                          </td>
                        </>
                      )}
                      {filterType !== "check_in" && (
                        <>
                          <td className="py-3 px-4 text-sm text-center text-gray-700 dark:text-gray-300 whitespace-nowrap font-semibold">
                            {record.check_out_time
                              ? formatTime(record.check_out_time)
                              : "-"}
                          </td>
                          <td className="py-3 px-4 text-sm text-center text-gray-600 dark:text-gray-400 w-48">
                            <div
                              className="w-48 truncate overflow-hidden whitespace-nowrap mx-auto"
                              title={record.check_out_location}
                            >
                              {record.check_out_location || "-"}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            {getStatusBadge(checkOutApproval)}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={filterType === "all" ? 8 : 5}
                    className="text-center py-8"
                  >
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium font-poppins">
                        No attendance records found
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approvers Modal */}
      {showApproversModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4"
          onClick={() => setShowApproversModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
              <h2 className="text-xl font-semibold dark:text-white font-poppins">
                My Approvers
              </h2>
              <button
                onClick={() => setShowApproversModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {approversData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">
                          Sr No.
                        </th>
                        <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">
                          Approval Type
                        </th>
                        <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">
                          Primary Approver
                        </th>
                        <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">
                          Secondary Approver
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {approversData.slice(0, 1).map((approval, index) => (
                        <tr
                          key={approval.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300 font-medium">
                            {index + 1}
                          </td>
                          <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            {approval.approval_module_id || "-"}
                          </td>
                          {/* <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                              {approval.approval_level_id || "-"}
                            </span>
                          </td> */}
                          <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            <span className="px-2 py-1 rounded-full text-md ">
                              {approval.primary_approver_id || "-"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            <span className="px-2 py-1 rounded-full text-md ">
                              {approval.secondary_approver_id || "-"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400 font-poppins">
                    No approvers found.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t dark:border-gray-700">
              <button
                onClick={() => setShowApproversModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-poppins"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
